/**
 * Done. / ChiroCPD — ProgressBar Primitive
 *
 * WCAG 2.1 AA: role="progressbar", aria-valuenow/min/max, aria-label.
 * Animated fill with motion-safe guard (prefers-reduced-motion respected in CSS).
 * Warning threshold: bar turns amber at ≥ 80% by default.
 */

import { cx } from '@/lib/utils';

interface ProgressBarProps {
  current: number;
  max: number;
  label: string;
  /** Percentage (0–100) at which the bar shows warning colour. Default 80. */
  warningThreshold?: number;
  /** Show numeric label below the bar */
  showLabel?: boolean;
  className?: string;
}

export function ProgressBar({
  current,
  max,
  label,
  warningThreshold = 80,
  showLabel = true,
  className,
}: ProgressBarProps): React.ReactElement {
  const safeMax = max > 0 ? max : 1;
  const percentage = Math.min(100, Math.round((current / safeMax) * 100));
  const isWarning = percentage >= warningThreshold && percentage < 100;
  const isExhausted = percentage >= 100;

  const fillColour = isExhausted
    ? 'bg-[var(--color-success-500)]'
    : isWarning
      ? 'bg-[var(--color-cpd-amber)]'
      : 'bg-[var(--color-done-500)]';

  return (
    <div className={cx('w-full', className)}>
      <div
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
        className="h-3 w-full rounded-full bg-[var(--surface-muted)] overflow-hidden"
      >
        <div
          className={cx('h-full rounded-full progress-fill', fillColour)}
          style={{ width: `${String(percentage)}%` }}
        />
      </div>

      {showLabel && (
        <p className="mt-1 text-xs text-[var(--text-secondary)]" aria-hidden="true">
          {current} / {max} hrs
        </p>
      )}
    </div>
  );
}
